/**
 * Debug Logger Utility
 * 
 * Comprehensive logging system for debugging UI display issues
 * Provides different log levels, component tracking, performance monitoring,
 * and state change detection
 */

/**
 * Log levels for categorizing different types of messages
 */
export enum LogLevel {
  ERROR = 'ERROR',
  WARN = 'WARN',
  INFO = 'INFO',
  DEBUG = 'DEBUG',
  TRACE = 'TRACE'
}

/**
 * Log entry interface for structured logging
 */
export interface LogEntry {
  timestamp: number
  level: LogLevel
  component: string
  message: string
  data?: any
  stack?: string | undefined
}

/**
 * Performance metric tracking
 */
export interface PerformanceMetric {
  component: string
  operation: string
  duration: number
  timestamp: number
}

/**
 * Component lifecycle event types
 */
export type LifecycleEvent = 
  | 'mount'
  | 'unmount'
  | 'render'
  | 'update'
  | 'error';

/**
 * Debug configuration settings
 */
interface DebugConfig {
  enabled: boolean
  logLevel: LogLevel
  persistLogs: boolean
  maxLogs: number
  showTimestamps: boolean
  showStackTraces: boolean
  componentTracking: boolean
  performanceTracking: boolean
}

/**
 * DebugLogger class - Main debugging utility
 * Handles all logging, tracking, and debugging operations
 */
class DebugLogger {
  private logs: LogEntry[] = []
  private performanceMetrics: PerformanceMetric[] = []
  private componentRenderCounts: Map<string, number> = new Map()
  private componentTimings: Map<string, number> = new Map()
  private subscribers: Set<(logs: LogEntry[]) => void> = new Set()
  
  private config: DebugConfig = {
    enabled: true,
    logLevel: LogLevel.DEBUG,
    persistLogs: true,
    maxLogs: 500,
    showTimestamps: true,
    showStackTraces: true,
    componentTracking: true,
    performanceTracking: true
  }

  /**
   * Update debug configuration
   * Allows runtime configuration of debug settings
   */
  configure(updates: Partial<DebugConfig>): void {
    this.config = { ...this.config, ...updates }
    this.log(LogLevel.INFO, 'DebugLogger', 'Configuration updated', updates)
  }

  /**
   * Get current configuration
   * Returns the current debug settings
   */
  getConfig(): DebugConfig {
    return { ...this.config }
  }

  /**
   * Check if a log level should be logged based on current config
   */
  private shouldLog(level: LogLevel): boolean {
    if (!this.config.enabled) return false
    
    const levels = [LogLevel.ERROR, LogLevel.WARN, LogLevel.INFO, LogLevel.DEBUG, LogLevel.TRACE]
    const currentLevelIndex = levels.indexOf(this.config.logLevel)
    const messageLevelIndex = levels.indexOf(level)
    
    return messageLevelIndex <= currentLevelIndex
  }

  /**
   * Core logging method
   * Records log entry and notifies subscribers
   */
  private log(level: LogLevel, component: string, message: string, data?: any): void {
    if (!this.shouldLog(level)) return

    const entry: LogEntry = {
      timestamp: Date.now(),
      level,
      component,
      message,
      data: data ? this.serializeData(data) : undefined,
      stack: (this.config.showStackTraces && level === LogLevel.ERROR
        ? new Error().stack
        : undefined) as string | undefined
    }

    // Add to logs array
    this.logs.push(entry)
    
    // Trim logs if exceeds max
    if (this.logs.length > this.config.maxLogs) {
      this.logs = this.logs.slice(-this.config.maxLogs)
    }

    // Store in localStorage if enabled
    if (this.config.persistLogs) {
      this.persistToStorage()
    }

    // Console output with color coding
    this.consoleOutput(entry)

    // Notify subscribers
    this.notifySubscribers()
  }

  /**
   * Serialize data for logging, handling circular references
   */
  private serializeData(data: any): any {
    try {
      // Handle special types
      if (data instanceof Error) {
        return {
          name: data.name,
          message: data.message,
          stack: data.stack
        }
      }
      
      // Use JSON stringify with circular reference handler
      return JSON.parse(JSON.stringify(data, this.getCircularReplacer()))
    } catch (err) {
      return { error: 'Failed to serialize data', original: String(data) }
    }
  }

  /**
   * Handle circular references in JSON stringify
   */
  private getCircularReplacer() {
    const seen = new WeakSet()
    return (_key: string, value: any) => {
      if (typeof value === 'object' && value !== null) {
        if (seen.has(value)) {
          return '[Circular]'
        }
        seen.add(value)
      }
      return value
    }
  }

  /**
   * Output log to console with appropriate styling
   */
  private consoleOutput(entry: LogEntry): void {
    const timestamp = this.config.showTimestamps
      ? (new Date(entry.timestamp).toISOString().split('T')[1]?.slice(0, -1) || '')
      : ''
    
    const prefix = `[${entry.level}] ${timestamp} [${entry.component}]`
    
    const styles: Record<LogLevel, string> = {
      [LogLevel.ERROR]: 'color: #ff0080; font-weight: bold',
      [LogLevel.WARN]: 'color: #ffaa00; font-weight: bold',
      [LogLevel.INFO]: 'color: #00e0ff',
      [LogLevel.DEBUG]: 'color: #00ff88',
      [LogLevel.TRACE]: 'color: #888888'
    }

    console.log(`%c${prefix}%c ${entry.message}`, styles[entry.level], 'color: inherit', entry.data || '')
    
    if (entry.stack) {
      console.log('Stack trace:', entry.stack)
    }
  }

  /**
   * Persist logs to localStorage
   */
  private persistToStorage(): void {
    try {
      const recentLogs = this.logs.slice(-100) // Only store last 100
      localStorage.setItem('debug_logs', JSON.stringify(recentLogs))
    } catch (err) {
      console.warn('Failed to persist logs to storage', err)
    }
  }

  /**
   * Load logs from localStorage
   */
  loadFromStorage(): void {
    try {
      const stored = localStorage.getItem('debug_logs')
      if (stored) {
        const parsed = JSON.parse(stored)
        this.logs = [...parsed, ...this.logs]
        this.log(LogLevel.INFO, 'DebugLogger', `Loaded ${parsed.length} logs from storage`)
      }
    } catch (err) {
      this.error('DebugLogger', 'Failed to load logs from storage', err)
    }
  }

  /**
   * Subscribe to log updates
   */
  subscribe(callback: (logs: LogEntry[]) => void): () => void {
    this.subscribers.add(callback)
    // Return unsubscribe function
    return () => {
      this.subscribers.delete(callback)
    }
  }

  /**
   * Notify all subscribers of log updates
   */
  private notifySubscribers(): void {
    this.subscribers.forEach(callback => {
      try {
        callback([...this.logs])
      } catch (err) {
        console.error('Error notifying subscriber', err)
      }
    })
  }

  /**
   * Public logging methods for different levels
   */
  
  error(component: string, message: string, data?: any): void {
    this.log(LogLevel.ERROR, component, message, data)
  }

  warn(component: string, message: string, data?: any): void {
    this.log(LogLevel.WARN, component, message, data)
  }

  info(component: string, message: string, data?: any): void {
    this.log(LogLevel.INFO, component, message, data)
  }

  debug(component: string, message: string, data?: any): void {
    this.log(LogLevel.DEBUG, component, message, data)
  }

  trace(component: string, message: string, data?: any): void {
    this.log(LogLevel.TRACE, component, message, data)
  }

  /**
   * Component lifecycle tracking
   * Track mount, unmount, render, and update events
   */
  lifecycle(component: string, event: LifecycleEvent, data?: any): void {
    if (!this.config.componentTracking) return

    const message = `Lifecycle: ${event.toUpperCase()}`
    this.debug(component, message, data)

    // Update render count
    if (event === 'render') {
      const count = (this.componentRenderCounts.get(component) || 0) + 1
      this.componentRenderCounts.set(component, count)
    }
  }

  /**
   * Get component render count
   */
  getRenderCount(component: string): number {
    return this.componentRenderCounts.get(component) || 0
  }

  /**
   * Performance timing start
   * Begin tracking an operation's duration
   */
  timeStart(component: string, operation: string): void {
    if (!this.config.performanceTracking) return
    
    const key = `${component}:${operation}`
    this.componentTimings.set(key, performance.now())
    this.trace(component, `⏱️ Started: ${operation}`)
  }

  /**
   * Performance timing end
   * Complete timing and record metric
   */
  timeEnd(component: string, operation: string): number | null {
    if (!this.config.performanceTracking) return null
    
    const key = `${component}:${operation}`
    const startTime = this.componentTimings.get(key)
    
    if (startTime === undefined) {
      this.warn(component, `No start time found for operation: ${operation}`)
      return null
    }

    const duration = performance.now() - startTime
    this.componentTimings.delete(key)

    // Record metric
    const metric: PerformanceMetric = {
      component,
      operation,
      duration,
      timestamp: Date.now()
    }
    
    this.performanceMetrics.push(metric)
    
    // Trim metrics
    if (this.performanceMetrics.length > 1000) {
      this.performanceMetrics = this.performanceMetrics.slice(-1000)
    }

    this.debug(component, `⏱️ Completed: ${operation} (${duration.toFixed(2)}ms)`)
    
    return duration
  }

  /**
   * Get all logs
   */
  getLogs(): LogEntry[] {
    return [...this.logs]
  }

  /**
   * Get logs filtered by component
   */
  getLogsByComponent(component: string): LogEntry[] {
    return this.logs.filter(log => log.component === component)
  }

  /**
   * Get logs filtered by level
   */
  getLogsByLevel(level: LogLevel): LogEntry[] {
    return this.logs.filter(log => log.level === level)
  }

  /**
   * Get performance metrics
   */
  getPerformanceMetrics(): PerformanceMetric[] {
    return [...this.performanceMetrics]
  }

  /**
   * Get performance metrics for a component
   */
  getComponentMetrics(component: string): PerformanceMetric[] {
    return this.performanceMetrics.filter(m => m.component === component)
  }

  /**
   * Get average performance for an operation
   */
  getAveragePerformance(component: string, operation: string): number | null {
    const metrics = this.performanceMetrics.filter(
      m => m.component === component && m.operation === operation
    )
    
    if (metrics.length === 0) return null
    
    const sum = metrics.reduce((acc, m) => acc + m.duration, 0)
    return sum / metrics.length
  }

  /**
   * Clear all logs
   */
  clearLogs(): void {
    this.logs = []
    this.performanceMetrics = []
    this.componentRenderCounts.clear()
    this.componentTimings.clear()
    
    try {
      localStorage.removeItem('debug_logs')
    } catch (err) {
      console.warn('Failed to clear storage', err)
    }
    
    this.info('DebugLogger', 'All logs cleared')
    this.notifySubscribers()
  }

  /**
   * Export logs as JSON
   */
  exportLogs(): string {
    return JSON.stringify({
      logs: this.logs,
      metrics: this.performanceMetrics,
      renderCounts: Array.from(this.componentRenderCounts.entries()),
      timestamp: Date.now()
    }, null, 2)
  }

  /**
   * Get summary statistics
   */
  getSummary() {
    const errorCount = this.logs.filter(l => l.level === LogLevel.ERROR).length
    const warnCount = this.logs.filter(l => l.level === LogLevel.WARN).length
    const components = new Set(this.logs.map(l => l.component))
    
    return {
      totalLogs: this.logs.length,
      errors: errorCount,
      warnings: warnCount,
      components: components.size,
      componentList: Array.from(components),
      performanceMetrics: this.performanceMetrics.length,
      renderCounts: Array.from(this.componentRenderCounts.entries())
        .sort((a, b) => b[1] - a[1])
        .slice(0, 10)
    }
  }
}

// Create singleton instance
const debugLogger = new DebugLogger()

// Enable debug mode based on environment or URL parameter
if (typeof window !== 'undefined') {
  const urlParams = new URLSearchParams(window.location.search)
  const debugParam = urlParams.get('debug')
  
  if (debugParam === 'true' || process.env.NODE_ENV === 'development') {
    debugLogger.configure({ enabled: true })
    debugLogger.info('DebugLogger', '🐛 Debug mode enabled')
  }
  
  // Load persisted logs
  debugLogger.loadFromStorage()
  
  // Expose to window for console access
  ;(window as any).debugLogger = debugLogger
}

export default debugLogger